/**
 * MilestoneGrid - Job milestone progress grid
 *
 * Shows a grid of jobs with their milestone completion status:
 * - WC Stage (PA, Assembly, Ship)
 * - PDF Proof (Op 38 - PROOF-PDF)
 * - Proof Approval (Op 40 - PDF APPROVAL)
 * - Printed (Op 87 - PRINTS-DONE)
 * - Opti Work (OPTI work center operations)
 * - Assemble (no JobBoss step yet)
 * - Ship (no JobBoss step yet)
 *
 * Phase 2C Enhancements:
 * - Active worker indicator on current gate
 * - Blocker indicators (e.g., "PDF Approval Pending")
 * - Expandable rows for routing detail
 *
 * Work Center Stage Logic:
 * - If any of PDF Proof, Proof Approval, Printed incomplete → "PA"
 * - If PA complete but Opti/Assemble incomplete → "Assembly"
 * - If all complete → "Shipping"
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Check, Minus, Circle, RefreshCw, ChevronDown, ChevronRight, User, AlertCircle } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Milestone operation definitions
const MILESTONES = {
  PDF_PROOF: { operationNumber: 38, operationCode: 'PROOF-PDF', label: 'PDF Proof', zone: 'PA' },
  PROOF_APPROVAL: { operationNumber: 40, operationCode: 'PDF APPROVAL', label: 'Proof Approval', zone: 'PA' },
  PRINTED: { operationNumber: 87, operationCode: 'PRINTS DONE', label: 'Printed', zone: 'PA' },
  OPTI_WORK: { workCenter: 'OPTI', label: 'Opti Work', zone: 'ASSEMBLY' },
  ASSEMBLE: { label: 'Assemble', zone: 'ASSEMBLY', noJobBoss: true },
  SHIP: { label: 'Ship', zone: 'SHIPPING', noJobBoss: true },
};

// Zone colors for visual grouping (Brand-Semantic Flow)
// Purple (brand/creative) → Amber (in-progress) → Green (done/ship)
const ZONE_COLORS = {
  PA: 'bg-violet-50 border-violet-200',
  ASSEMBLY: 'bg-amber-50 border-amber-200',
  SHIPPING: 'bg-emerald-50 border-emerald-200',
};

const ZONE_HEADER_COLORS = {
  PA: 'bg-violet-100 text-violet-700',
  ASSEMBLY: 'bg-amber-100 text-amber-700',
  SHIPPING: 'bg-emerald-100 text-emerald-700',
};

// Blocker type display labels
const BLOCKER_LABELS = {
  PDF_APPROVAL_PENDING: 'Awaiting PDF Approval',
  MATERIAL_WAIT: 'Waiting for Materials',
  CLIENT_FEEDBACK: 'Awaiting Client Feedback',
  VENDOR_DELAY: 'Vendor Delay',
  REWORK_NEEDED: 'Rework Needed',
  QA_HOLD: 'QA Hold',
  OTHER: 'On Hold',
};

// Format time ago
function formatTimeAgo(date) {
  if (!date) return '';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Check if an operation is complete based on routing data
 * Uses .some() to check if ANY matching operation is complete
 * (routing can have duplicate operation codes from multiple line items)
 */
function isOperationComplete(routing, operationNumber, operationCode) {
  if (!routing || !Array.isArray(routing)) return false;

  // Check if ANY operation with this code/number is complete
  return routing.some(r => {
    const matches = r.operationNumber === operationNumber ||
      r.opNumber === operationNumber ||
      r.operationCode === operationCode;

    if (!matches) return false;

    return r.complete === true ||
      r.status === 'Complete' ||
      r.status === 'Closed' ||
      r.status === 'Finished' ||
      r.status === 'C';
  });
}

// Operations that apply to all work centers and should be excluded from milestone checks
const EXCEPTION_OPERATIONS = ['REVISION', 'REWORK'];

/**
 * Check if an operation belongs to OPTI work center
 * Excludes exception operations (REVISION, REWORK) which are assigned to all work centers
 */
function isOptiOperation(r) {
  // Skip exception operations - they're not real OPTI workflow steps
  if (EXCEPTION_OPERATIONS.includes(r.operationCode)) {
    return false;
  }

  // Check workCenters array (primary - from tiered sync)
  if (Array.isArray(r.workCenters) && r.workCenters.includes('OPTI')) {
    return true;
  }
  // Fallback checks for different data formats
  if (r.workCenter === 'OPTI' || r.workCenterId === 'OPTI') {
    return true;
  }
  return false;
}

/**
 * Check if job has OPTI operations in routing
 */
function hasOptiOperations(routing) {
  if (!routing || !Array.isArray(routing)) return false;

  return routing.some(r => isOptiOperation(r));
}

/**
 * Check if ALL OPTI operations are complete
 * Returns true only when every OPTI operation is finished
 */
function isOptiComplete(routing) {
  if (!routing || !Array.isArray(routing)) return false;

  const optiOps = routing.filter(r => isOptiOperation(r));

  if (optiOps.length === 0) return false;

  return optiOps.every(op =>
    op.complete === true ||
    op.status === 'Complete' ||
    op.status === 'Closed' ||
    op.status === 'Finished' ||
    op.status === 'C'
  );
}

/**
 * Map backend stage (from stageCalculationService) to display-friendly WC stage
 *
 * Backend stages: PENDING, PROD_ART, PRINTING, ASSEMBLY, SHIPPING, COMPLETE
 * Display stages: PA, Assembly, Shipping, Complete
 *
 * This ensures the UI uses the authoritative stage from the backend
 * while presenting it in a user-friendly format.
 */
function mapBackendStageToWCStage(backendStage) {
  const stageMap = {
    'PENDING': 'PA',
    'PROD_ART': 'PA',
    'PRINTING': 'PA',
    'ASSEMBLY': 'Assembly',
    'SHIPPING': 'Shipping',
    'COMPLETE': 'Complete',
    'ARCHIVED': 'Complete',
  };
  return stageMap[backendStage] || 'PA';
}

/**
 * Calculate work center stage from milestone status (fallback for legacy data)
 * Used when backend stage is not available
 */
function calculateWCStage(milestoneStatus) {
  const { pdfProof, proofApproval, printed, optiWork, hasOpti } = milestoneStatus;

  // If any PA milestone is incomplete, stage is PA
  if (!pdfProof || !proofApproval || !printed) {
    return 'PA';
  }

  // PA is complete, check Assembly zone
  // If has Opti and Opti not complete, or Assemble not complete (when we track it)
  if (hasOpti && !optiWork) {
    return 'Assembly';
  }

  // If no Opti or Opti complete, still could be Assembly if assemble step exists
  // For now, if PA complete, show Assembly until we have ship tracking
  return 'Assembly';
}

/**
 * Active Worker Indicator - Shows who's working on a job
 */
function ActiveWorkerIndicator({ workers }) {
  if (!workers || workers.length === 0) return null;

  const primary = workers[0];
  const others = workers.length - 1;

  // Activity status colors
  const statusColors = {
    active_now: 'bg-green-500',
    active_recently: 'bg-yellow-500',
    stale: 'bg-gray-400',
  };

  const statusLabels = {
    active_now: 'Active now',
    active_recently: `Active ${formatTimeAgo(primary.lastActivity)}`,
    stale: `Last seen ${formatTimeAgo(primary.lastActivity)}`,
  };

  return (
    <div className="flex items-center gap-1 text-xs">
      <div className={`w-2 h-2 rounded-full ${statusColors[primary.activityStatus] || 'bg-gray-400'}`} />
      <span className="text-gray-700 truncate max-w-[80px]" title={primary.employeeName}>
        {primary.employeeName?.split(' ')[0] || primary.employeeId}
      </span>
      {others > 0 && (
        <span className="text-gray-400">+{others}</span>
      )}
    </div>
  );
}

/**
 * Blocker Badge - Shows project blocker status
 */
function BlockerBadge({ blocker }) {
  if (!blocker) return null;

  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded-md">
      <AlertCircle className="w-3 h-3 text-red-500" />
      <span className="text-xs text-red-700 font-medium">
        {BLOCKER_LABELS[blocker] || blocker}
      </span>
    </div>
  );
}

/**
 * Milestone checkbox component - Enhanced with active worker support
 */
function MilestoneCheck({ complete, notApplicable, label, activeWorker, isCurrentGate }) {
  if (notApplicable) {
    return (
      <div className="flex flex-col items-center justify-center gap-1" title={`${label}: N/A`}>
        <Minus className="w-4 h-4 text-gray-300" />
      </div>
    );
  }

  if (complete) {
    return (
      <div className="flex flex-col items-center justify-center gap-1" title={`${label}: Complete`}>
        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </div>
      </div>
    );
  }

  // In progress - show worker if active
  return (
    <div className="flex flex-col items-center justify-center gap-1" title={`${label}: Pending`}>
      <div className={`w-5 h-5 rounded-full ${isCurrentGate ? 'bg-blue-500 ring-2 ring-blue-200' : 'border-2 border-gray-300 bg-white'} flex items-center justify-center`}>
        {isCurrentGate && activeWorker && (
          <User className="w-3 h-3 text-white" />
        )}
      </div>
      {isCurrentGate && activeWorker && (
        <div className="text-[9px] text-blue-600 font-medium truncate max-w-[60px]">
          {activeWorker.employeeName?.split(' ')[0] || 'Working'}
        </div>
      )}
    </div>
  );
}

/**
 * Work Center Stage badge
 */
function WCStageBadge({ stage, backendStage }) {
  const colors = {
    PA: 'bg-violet-100 text-violet-700 border-violet-300',
    Assembly: 'bg-amber-100 text-amber-700 border-amber-300',
    Shipping: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    Complete: 'bg-green-100 text-green-700 border-green-300',
  };

  // Show more granular backend stage as tooltip
  const tooltip = backendStage ? `Stage: ${backendStage}` : '';

  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[stage] || 'bg-gray-100 text-gray-600'}`}
      title={tooltip}
    >
      {stage}
    </span>
  );
}

/**
 * Routing Detail - Expandable section showing full routing steps
 * Shows ALL routing steps (complete and incomplete) with step numbers prominently displayed
 */
function RoutingDetail({ routing, activeWorkers }) {
  if (!routing || routing.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No routing data available
      </div>
    );
  }

  // Sort routing by step number (sequential order)
  const sortedRouting = [...routing].sort((a, b) => {
    const aNum = a.stepNumber || a.operationNumber || a.sequence || a.opNumber || 0;
    const bNum = b.stepNumber || b.operationNumber || b.sequence || b.opNumber || 0;
    return aNum - bNum;
  });

  // Find active worker for each operation
  const getWorkerForOp = (stepNum, opCode) => {
    return activeWorkers?.find(w =>
      w.operationNumber === stepNum || w.stepNumber === stepNum || w.operationCode === opCode
    );
  };

  // Status color mapping
  const getStatusColor = (status, isComplete) => {
    if (isComplete || status === 'Complete' || status === 'Finished' || status === 'Closed' || status === 'C') {
      return 'text-emerald-600 font-medium';
    }
    if (status === 'Current' || status === 'Started' || status === 'In Progress' || status === 'O') {
      return 'text-blue-600 font-medium';
    }
    if (status === 'Future' || status === 'Pending') {
      return 'text-gray-400';
    }
    return 'text-gray-500';
  };

  const getStatusIcon = (status, isComplete) => {
    if (isComplete || status === 'Complete' || status === 'Finished' || status === 'Closed' || status === 'C') {
      return <Check className="w-4 h-4 text-emerald-500" />;
    }
    if (status === 'Current' || status === 'Started' || status === 'In Progress' || status === 'O') {
      return <Circle className="w-4 h-4 text-blue-500 fill-blue-500" />;
    }
    // Future/Pending - empty circle
    return <Circle className="w-4 h-4 text-gray-300" />;
  };

  const getRowBackground = (status, isComplete) => {
    if (isComplete || status === 'Complete' || status === 'Finished' || status === 'Closed') {
      return 'bg-emerald-50/50';
    }
    if (status === 'Current') {
      return 'bg-blue-50/50';
    }
    return '';
  };

  // Count completed vs total
  const completedCount = sortedRouting.filter(s =>
    s.complete || s.isComplete || s.status === 'Complete' || s.status === 'Finished' || s.status === 'Closed'
  ).length;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-gray-600">Routing Detail</div>
        <div className="text-xs text-gray-500">
          {completedCount} of {sortedRouting.length} complete
        </div>
      </div>
      <div className="grid gap-0.5">
        {sortedRouting.map((step, idx) => {
          const stepNum = step.stepNumber || step.operationNumber || step.opNumber;
          const opCode = step.operationCode || step.code || '';
          const status = step.status || 'Pending';
          const isComplete = step.complete || step.isComplete;
          const worker = getWorkerForOp(stepNum, opCode);

          return (
            <div
              key={`${stepNum}-${idx}`}
              className={`flex items-center gap-3 py-1.5 px-2 rounded ${getRowBackground(status, isComplete)} hover:bg-slate-100`}
            >
              {/* Status Icon */}
              <div className="flex-shrink-0 w-5">
                {getStatusIcon(status, isComplete)}
              </div>

              {/* Step Number - Prominent */}
              <div className="flex-shrink-0 w-12">
                <span className="text-sm font-mono font-semibold text-gray-700">{stepNum}</span>
              </div>

              {/* Operation Code & Description */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">{opCode}</span>
                  {step.workCenter && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                      {step.workCenter}
                    </span>
                  )}
                </div>
                {step.description && step.description !== opCode && (
                  <div className="text-[10px] text-gray-400 truncate">{step.description}</div>
                )}
              </div>

              {/* Status & Worker */}
              <div className="flex items-center gap-2 flex-shrink-0 min-w-[100px] justify-end">
                <span className={`text-xs ${getStatusColor(status, isComplete)}`}>
                  {status}
                </span>
                {worker && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-[10px] text-blue-700 font-medium">
                      {worker.employeeName?.split(' ')[0] || 'Active'}
                    </span>
                  </div>
                )}
              </div>

              {/* Completion Time */}
              {step.completedAt && (
                <div className="text-[10px] text-gray-400 flex-shrink-0 w-16 text-right">
                  {formatTimeAgo(step.completedAt)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MilestoneGrid({ quoteNumber, jobs: jobsProp, token }) {
  const [jobs, setJobs] = useState(jobsProp || []);
  const [jobRoutings, setJobRoutings] = useState({});
  const [jobStages, setJobStages] = useState({});           // Backend-calculated stages per job
  const [jobMilestones, setJobMilestones] = useState({});   // Backend-calculated milestones per job
  const [activeWorkersByJob, setActiveWorkersByJob] = useState({});
  const [projectBlocker, setProjectBlocker] = useState(null);
  const [syncHealth, setSyncHealth] = useState(null);       // Sync freshness data from backend
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch job routing data with active workers and blockers
  const fetchJobRoutings = useCallback(async () => {
    if (!quoteNumber || jobs.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      // Try to fetch milestone data from job_stages (enhanced with active workers)
      const response = await fetch(
        `${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/milestones`,
        {
          headers: {
            Authorization: `Bearer ${token || sessionStorage.getItem('msal.backendToken')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[MilestoneGrid] Fetched data:', data.source, data.jobs?.length, 'jobs');

        // Map job number to routing data AND stage/milestones from backend
        const routingMap = {};
        const workersMap = {};
        const stageMap = {};
        const milestonesMap = {};

        (data.jobs || []).forEach(job => {
          // NO FALLBACK - routing comes from job_routing_steps only (authoritative)
          routingMap[job.jobNumber] = job.routing || [];

          // Store stage and milestones from backend calculation
          if (job.stage) {
            stageMap[job.jobNumber] = job.stage;
          }
          if (job.milestones) {
            milestonesMap[job.jobNumber] = job.milestones;
          }

          // Store active workers per job
          if (job.activeWorkers && job.activeWorkers.length > 0) {
            workersMap[job.jobNumber] = job.activeWorkers;
          }

          // Debug log for specific job
          if (job.jobNumber === '2001140-05') {
            console.log('[MilestoneGrid] Job 2001140-05:', { stage: job.stage, milestones: job.milestones });
          }
        });

        setJobRoutings(routingMap);
        setActiveWorkersByJob(workersMap);
        setJobStages(stageMap);
        setJobMilestones(milestonesMap);

        // Set project blocker if available
        if (data.projectBlocker) {
          setProjectBlocker(data.projectBlocker);
        }

        // Set sync health for freshness display
        if (data.syncHealth) {
          setSyncHealth(data.syncHealth);
        }
      } else {
        // No fallback - job_routing_steps is the authoritative source
        console.warn('Milestone endpoint not available');
        setError('Unable to load milestone data');
      }
    } catch (err) {
      console.error('Error fetching job routings:', err);
      setError('Failed to load milestone data');
    } finally {
      setLoading(false);
    }
  }, [quoteNumber, jobs.length, token]);

  // Toggle row expansion
  const toggleRowExpansion = (jobNumber) => {
    setExpandedRows(prev => ({
      ...prev,
      [jobNumber]: !prev[jobNumber],
    }));
  };

  useEffect(() => {
    fetchJobRoutings();
  }, [fetchJobRoutings]);

  // Manual refresh from JobBoss
  const handleRefresh = async () => {
    if (refreshing || !quoteNumber) return;

    setRefreshing(true);
    setError(null);

    try {
      // Call the refresh endpoint to pull fresh data from JobBoss
      const refreshResponse = await fetch(
        `${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/refresh`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token || sessionStorage.getItem('msal.backendToken')}`,
          },
        }
      );

      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh from JobBoss');
      }

      const result = await refreshResponse.json();
      console.log('Refresh result:', result);

      // Re-fetch the milestone data after refresh
      await fetchJobRoutings();

    } catch (err) {
      console.error('Error refreshing project data:', err);
      setError('Failed to refresh data from JobBoss');
    } finally {
      setRefreshing(false);
    }
  };

  // Update jobs when prop changes
  useEffect(() => {
    if (jobsProp) {
      setJobs(jobsProp);
    }
  }, [jobsProp]);

  // Calculate milestones for each job
  const jobsWithMilestones = jobs.map(job => {
    // NO FALLBACK - routing comes from job_routing_steps only (authoritative)
    const routing = jobRoutings[job.jobNumber] || [];
    const hasOpti = hasOptiOperations(routing);
    const workers = activeWorkersByJob[job.jobNumber] || [];

    // Use backend-provided milestones if available (from fetched data or job prop)
    // Priority: fetched jobMilestones > job.milestones prop > local calculation
    const backendMilestones = jobMilestones[job.jobNumber] || job.milestones || {};
    const milestoneStatus = {
      pdfProof: backendMilestones.proof_pdf_started ?? isOperationComplete(routing, 38, 'PROOF-PDF'),
      proofApproval: backendMilestones.pdf_approval_complete ?? isOperationComplete(routing, 40, 'PDF APPROVAL'),
      printed: backendMilestones.prints_done_complete ?? isOperationComplete(routing, 87, 'PRINTS DONE'),
      optiWork: hasOpti ? isOptiComplete(routing) : null,
      hasOpti,
      // Assembly: complete if assembly_complete OR pop_packout_complete (packout implies assembly done)
      assemble: backendMilestones.assembly_complete || backendMilestones.pop_packout_complete || null,
      // Ship: NOT YET TRACKED for completion - will be implemented when we have actual shipping confirmation
      // But if assembly is done, Ship becomes the current gate (false = pending, not null = N/A)
      ship: (backendMilestones.assembly_complete || backendMilestones.pop_packout_complete) ? false : null,
    };

    // Use backend stage if available (from fetched data or job prop)
    // Priority: fetched jobStages > job.stage prop > local calculation
    const backendStage = jobStages[job.jobNumber] || job.stage;
    const wcStage = backendStage
      ? mapBackendStageToWCStage(backendStage)
      : calculateWCStage(milestoneStatus);

    // Determine current gate (first incomplete milestone)
    let currentGate = null;
    if (!milestoneStatus.pdfProof) currentGate = 'pdfProof';
    else if (!milestoneStatus.proofApproval) currentGate = 'proofApproval';
    else if (!milestoneStatus.printed) currentGate = 'printed';
    else if (hasOpti && !milestoneStatus.optiWork) currentGate = 'optiWork';
    else if (!milestoneStatus.assemble) currentGate = 'assemble';
    else if (!milestoneStatus.ship) currentGate = 'ship';
    else currentGate = 'complete';

    // Find active worker for current gate by matching operation
    const currentWorker = workers.find(w => {
      if (currentGate === 'pdfProof' && w.operationNumber === 38) return true;
      if (currentGate === 'proofApproval' && w.operationNumber === 40) return true;
      if (currentGate === 'printed' && w.operationNumber === 87) return true;
      if (currentGate === 'optiWork' && w.operationCode?.includes('OPTI')) return true;
      return false;
    }) || workers[0]; // Fall back to first active worker

    // Include out-of-order info from backend (process compliance issues)
    const outOfOrder = job.outOfOrder || null;

    return {
      ...job,
      milestoneStatus,
      wcStage,
      backendStage: backendStage || null, // Use the resolved backend stage for tooltip
      hasOpti,
      currentGate,
      activeWorkers: workers,
      currentWorker,
      outOfOrder, // { outOfOrder: bool, incompleteSteps: [], description: string }
      skuClass: job.skuClass || '2d',
    };
  });

  // Calculate totals
  const totals = {
    total: jobs.length,
    inPA: jobsWithMilestones.filter(j => j.wcStage === 'PA').length,
    inAssembly: jobsWithMilestones.filter(j => j.wcStage === 'Assembly').length,
    inShipping: jobsWithMilestones.filter(j => j.wcStage === 'Shipping').length,
    complete: jobsWithMilestones.filter(j => j.wcStage === 'Complete').length,
    outOfOrder: jobsWithMilestones.filter(j => j.outOfOrder?.outOfOrder || j.outOfOrder?.hasIncompleteMilestones).length,
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2" />
        Loading milestone data...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-slate-100 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-800">PROGRESS BY SKU</h3>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 hover:bg-slate-200 rounded transition-colors disabled:opacity-50"
            title="Refresh from JobBoss"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-500">{totals.total} jobs</span>
          {totals.inPA > 0 && (
            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded">
              {totals.inPA} in PA
            </span>
          )}
          {totals.inAssembly > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
              {totals.inAssembly} in Assembly
            </span>
          )}
          {totals.inShipping > 0 && (
            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
              {totals.inShipping} Shipping
            </span>
          )}
          {totals.complete > 0 && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
              {totals.complete} Complete
            </span>
          )}
          {totals.outOfOrder > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded" title="Jobs with out-of-order progress or missing milestones">
              {totals.outOfOrder} ⚠️
            </span>
          )}
          {/* Sync Freshness Indicator */}
          {syncHealth && (
            <span
              className={`px-2 py-0.5 rounded text-xs ${
                syncHealth.overallHealth === 'fresh' ? 'bg-green-100 text-green-700' :
                syncHealth.overallHealth === 'warming' ? 'bg-blue-100 text-blue-700' :
                syncHealth.overallHealth === 'stale' ? 'bg-amber-100 text-amber-700' :
                syncHealth.overallHealth === 'never_synced' ? 'bg-gray-100 text-gray-600' :
                'bg-red-100 text-red-700'
              }`}
              title={`Data freshness: ${syncHealth.overallHealth}${syncHealth.oldestSyncAt ? ` (oldest: ${new Date(syncHealth.oldestSyncAt).toLocaleTimeString()})` : ''}`}
            >
              {syncHealth.overallHealth === 'fresh' ? 'Synced' :
               syncHealth.overallHealth === 'warming' ? 'Warming' :
               syncHealth.overallHealth === 'stale' ? 'Stale' :
               syncHealth.overallHealth === 'never_synced' ? 'Not Synced' :
               'Sync Error'}
            </span>
          )}
        </div>
      </div>

      {/* Project Blocker Banner */}
      {projectBlocker?.currentBlocker && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 font-medium">
            Project Blocked: {BLOCKER_LABELS[projectBlocker.currentBlocker] || projectBlocker.currentBlocker}
          </span>
        </div>
      )}

      {error && (
        <div className="px-4 py-2 bg-amber-50 text-amber-700 text-xs">
          {error}
        </div>
      )}

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {/* Zone headers row */}
            <tr className="border-b border-gray-200">
              <th className="px-4 py-1 text-left" colSpan={3}></th>
              <th className={`px-2 py-1 text-center text-[10px] font-medium ${ZONE_HEADER_COLORS.PA}`} colSpan={3}>
                In PA
              </th>
              <th className={`px-2 py-1 text-center text-[10px] font-medium ${ZONE_HEADER_COLORS.ASSEMBLY}`} colSpan={2}>
                In Assembly
              </th>
              <th className={`px-2 py-1 text-center text-[10px] font-medium ${ZONE_HEADER_COLORS.SHIPPING}`} colSpan={1}>
                Ship
              </th>
            </tr>
            {/* Column headers row */}
            <tr className="text-xs text-gray-600 border-b border-gray-200 bg-slate-50">
              <th className="text-left px-3 py-2 font-medium w-24">Job #</th>
              <th className="text-left px-3 py-2 font-medium min-w-[280px]">SKU</th>
              <th className="text-center px-2 py-2 font-medium w-24">WC Stage</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.PA}`}>PDF Proof</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.PA}`}>Proof Appr.</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.PA}`}>Printed</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.ASSEMBLY}`}>Opti Work</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.ASSEMBLY}`}>Assemble</th>
              <th className={`text-center px-2 py-2 font-medium w-20 ${ZONE_COLORS.SHIPPING}`}>Ship</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {jobsWithMilestones.length > 0 ? (
              jobsWithMilestones.map((job, i) => (
                <React.Fragment key={job.jobNumber || job.sku || i}>
                  <tr
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleRowExpansion(job.jobNumber)}
                  >
                    {/* Expand Arrow + Job # */}
                    <td className="px-3 py-2 text-xs text-gray-700 font-mono">
                      <div className="flex items-center gap-1">
                        {expandedRows[job.jobNumber] ? (
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                        )}
                        {job.jobNumber || '—'}
                      </div>
                    </td>

                    {/* SKU Name + Active Worker Indicator */}
                    <td className="px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900 text-xs" title={job.sku || job.partNumber}>
                              {job.sku || job.partNumber || job.jobNumber}
                            </span>
                            {job.skuClass && job.skuClass !== '2d' && (
                              <span
                                className={`text-[9px] px-1 py-0.5 rounded ${
                                  job.skuClass === '3d'
                                    ? 'bg-blue-100 text-blue-600'
                                    : 'bg-purple-100 text-purple-600'
                                }`}
                                title={job.skuClass === '3d' ? '3D workflow' : '2D + 3D combined'}
                              >
                                {job.skuClass === '3d' ? '3D' : '2D+3D'}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate max-w-[200px]" title={job.description}>
                            {job.description || '—'}
                          </div>
                        </div>
                        {job.activeWorkers.length > 0 && (
                          <ActiveWorkerIndicator workers={job.activeWorkers} />
                        )}
                      </div>
                    </td>

                    {/* WC Stage */}
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <WCStageBadge stage={job.wcStage} backendStage={job.backendStage} />
                        {job.outOfOrder?.outOfOrder && (
                          <span
                            className="text-amber-500"
                            title={`Out of order: ${job.outOfOrder.description || 'Earlier steps incomplete'}`}
                          >
                            ⚠️
                          </span>
                        )}
                        {job.outOfOrder?.hasIncompleteMilestones && (
                          <span
                            className="text-red-500"
                            title={`Missing milestones: ${job.outOfOrder.description || 'Process compliance issue'}`}
                          >
                            🚩
                          </span>
                        )}
                      </div>
                    </td>

                    {/* PDF Proof */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.PA}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.pdfProof}
                        label="PDF Proof"
                        isCurrentGate={job.currentGate === 'pdfProof'}
                        activeWorker={job.currentGate === 'pdfProof' ? job.currentWorker : null}
                      />
                    </td>

                    {/* Proof Approval */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.PA}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.proofApproval}
                        label="Proof Approval"
                        isCurrentGate={job.currentGate === 'proofApproval'}
                        activeWorker={job.currentGate === 'proofApproval' ? job.currentWorker : null}
                      />
                    </td>

                    {/* Printed */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.PA}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.printed}
                        label="Printed"
                        isCurrentGate={job.currentGate === 'printed'}
                        activeWorker={job.currentGate === 'printed' ? job.currentWorker : null}
                      />
                    </td>

                    {/* Opti Work */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.ASSEMBLY}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.optiWork}
                        notApplicable={!job.hasOpti}
                        label="Opti Work"
                        isCurrentGate={job.currentGate === 'optiWork'}
                        activeWorker={job.currentGate === 'optiWork' ? job.currentWorker : null}
                      />
                    </td>

                    {/* Assemble */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.ASSEMBLY}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.assemble}
                        notApplicable={job.milestoneStatus.assemble === null}
                        label="Assemble"
                        isCurrentGate={job.currentGate === 'assemble'}
                        activeWorker={job.currentGate === 'assemble' ? job.currentWorker : null}
                      />
                    </td>

                    {/* Ship */}
                    <td className={`px-2 py-2 text-center ${ZONE_COLORS.SHIPPING}`}>
                      <MilestoneCheck
                        complete={job.milestoneStatus.ship}
                        notApplicable={job.milestoneStatus.ship === null}
                        label="Ship"
                        isCurrentGate={job.currentGate === 'ship'}
                        activeWorker={job.currentGate === 'ship' ? job.currentWorker : null}
                      />
                    </td>
                  </tr>

                  {/* Expanded Row - Routing Detail */}
                  {expandedRows[job.jobNumber] && (
                    <tr>
                      <td colSpan={9} className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                        <RoutingDetail
                          routing={jobRoutings[job.jobNumber] || []}
                          activeWorkers={job.activeWorkers}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No jobs loaded
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
